import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AlertCircle, CheckCircle, Tag, XCircle } from "lucide-react";
import {
  getMissedQuestionsRequest,
  getTopMissedQuestionsRequest,
} from "../../../modules/students-modules/questions/Actions";
import QuestionResourceActions from "../../../containers/student/exams/question-resource-actions";
import { studentPalette } from "../../../constants/studentPalette";
import { studentSurfaces } from "../../../constants/studentThemeSurfaces";
import { hasFetchedOnce } from "../../../utils/fetchFreshness";

const C = {
  primary: studentPalette.primary,
  secondary: studentPalette.secondary,
  crimson: studentPalette.crimson,
  cream: studentPalette.cream,
  border: studentSurfaces.border,
  paper: studentSurfaces.raised,
  muted: studentSurfaces.textMuted,
  text: studentSurfaces.text,
  sand: studentSurfaces.muted,
  correct: "#00AA8D",
  correctBg: "rgba(0, 170, 141, 0.08)",
  correctBgDark: "rgba(0, 170, 141, 0.12)",
  wrong: "#9B2C2C",
  wrongBg: "rgba(155, 44, 44, 0.08)",
  wrongBgDark: "rgba(245, 101, 101, 0.12)",
};

const darkHeadingSx = { "html[data-theme='dark'] &": { color: "var(--text-primary)" } };
const darkTextSx = { "html[data-theme='dark'] &": { color: "var(--text-primary)" } };
const darkQuestionHtmlSx = {
  "html[data-theme='dark'] &": { color: "var(--text-primary)" },
  "html[data-theme='dark'] & p, html[data-theme='dark'] & span, html[data-theme='dark'] & div, html[data-theme='dark'] & li":
    { color: "inherit" },
};

const scrollCss = {
  scrollbarWidth: "thin",
  scrollbarColor: "var(--scrollbar-thumb, #5B0D16) transparent",
};

const MissedQuestions = () => {
  const [activeTab, setActiveTab] = useState("top");
  const [selectedTags, setSelectedTags] = useState([]);
  const dispatch = useDispatch();
  const topMissed = useSelector((state) => state.questions.top_missed);
  const topMissedFetchedAt = useSelector((state) => state.questions.topMissedFetchedAt);
  const myMissed = useSelector((state) => state.questions.missed);

  useEffect(() => {
    dispatch(getMissedQuestionsRequest({ type: 1 }));
  }, [dispatch]);

  useEffect(() => {
    if (hasFetchedOnce(topMissedFetchedAt)) return;
    dispatch(getTopMissedQuestionsRequest());
  }, [dispatch, topMissedFetchedAt]);

  const allTags = useMemo(() => {
    const tagsMap = new Map();

    topMissed.forEach((questionData) => {
      const tags = questionData.tags || questionData.question?.tags || [];
      tags.forEach((tag) => {
        if (tag && tag.id && !tagsMap.has(tag.id)) {
          tagsMap.set(tag.id, tag);
        }
      });
    });

    myMissed.forEach((questionData) => {
      const tags = questionData.tags || questionData.question?.tags || [];
      tags.forEach((tag) => {
        if (tag && tag.id && !tagsMap.has(tag.id)) {
          tagsMap.set(tag.id, tag);
        }
      });
    });

    return Array.from(tagsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [topMissed, myMissed]);

  const filteredTopMissed = useMemo(() => {
    if (selectedTags.length === 0) return topMissed;

    return topMissed.filter((questionData) => {
      const tags = questionData.tags || questionData.question?.tags || [];
      const tagIds = tags.map((tag) => tag.id);
      return selectedTags.some((selectedTagId) => tagIds.includes(selectedTagId));
    });
  }, [topMissed, selectedTags]);

  const filteredMyMissed = useMemo(() => {
    if (selectedTags.length === 0) return myMissed;

    return myMissed.filter((questionData) => {
      const tags = questionData.tags || questionData.question?.tags || [];
      const tagIds = tags.map((tag) => tag.id);
      return selectedTags.some((selectedTagId) => tagIds.includes(selectedTagId));
    });
  }, [myMissed, selectedTags]);

  const handleTagToggle = (tagId) => {
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const renderQuestion = (questionData, index) => {
    const { question, answers, student_answer_id, chapter, wrong_count } = questionData;
    const tags = questionData.tags || question?.tags || [];

    return (
      <Box
        key={`${question.id || "noid"}-${index}`}
        bg={C.sand}
        borderWidth="1px"
        borderColor={C.border}
        borderRadius="lg"
        p={{ base: 4, md: 6 }}
        mb={4}
        sx={{ "html[data-theme='dark'] &": { bg: "var(--surface-muted)" } }}
      >
        <Flex align="flex-start" gap={4}>
          <Flex
            w="32px"
            h="32px"
            bg={C.primary}
            color={C.cream}
            borderRadius="md"
            align="center"
            justify="center"
            flexShrink={0}
            fontWeight="800"
            fontSize="sm"
          >
            {index + 1}
          </Flex>

          <Box flex="1" minW={0}>
            <Flex align="flex-start" justify="space-between" gap={3} mb={4}>
              <Box flex="1" minW={0}>
                <Flex align="center" flexWrap="wrap" gap={2} mb={2}>
                  {chapter && (
                    <Box
                      as="span"
                      display="inline-flex"
                      alignItems="center"
                      px={2.5}
                      py={0.5}
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight="700"
                      bg="rgba(112, 41, 51, 0.1)"
                      color={C.secondary}
                      borderWidth="1px"
                      borderColor="rgba(112, 41, 51, 0.2)"
                      sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
                    >
                      {chapter.name}
                    </Box>
                  )}

                  {wrong_count > 0 && (
                    <Box
                      as="span"
                      display="inline-flex"
                      alignItems="center"
                      px={2.5}
                      py={0.5}
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight="700"
                      bg={C.wrongBg}
                      color={C.wrong}
                      borderWidth="1px"
                      borderColor="rgba(155, 44, 44, 0.25)"
                    >
                      {wrong_count} Students missed this question
                    </Box>
                  )}

                  {tags?.length > 0 &&
                    tags.map((tag) => (
                      <Box
                        key={tag.id}
                        as="span"
                        display="inline-flex"
                        alignItems="center"
                        gap={1}
                        px={2.5}
                        py={0.5}
                        borderRadius="full"
                        fontSize="xs"
                        fontWeight="700"
                        bg="rgba(91, 13, 22, 0.06)"
                        color={C.primary}
                        borderWidth="1px"
                        borderColor="rgba(91, 13, 22, 0.15)"
                        sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
                      >
                        <Tag size={12} />
                        <span>{tag.name}</span>
                      </Box>
                    ))}
                </Flex>

                <Box
                  fontWeight="600"
                  color={C.text}
                  lineHeight="1.65"
                  sx={darkQuestionHtmlSx}
                  dangerouslySetInnerHTML={{ __html: question.question }}
                />
              </Box>
            </Flex>

            {question.image && (
              <Box mb={4}>
                <Image
                  src={`https://edutopiaonline.com/api/${question?.image}`}
                  alt="Question"
                  maxH="192px"
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={C.border}
                />
              </Box>
            )}

            <QuestionResourceActions
              question={question}
              variant="review"
              design="missed"
              showAllButtons
            />

            <VStack align="stretch" spacing={3}>
              {answers.map((answer, answerIndex) => {
                const isCorrect = answer?.right_answer === 1;
                const isStudentAnswer =
                  student_answer_id && answer?.id && student_answer_id === answer?.id;
                const isWrongStudentAnswer = isStudentAnswer && !isCorrect;

                let borderColor = C.border;
                let bgColor = C.paper;
                let letterBg = C.sand;
                let letterColor = C.primary;
                let textColor = C.text;
                let choiceSx = {
                  "html[data-theme='dark'] &": {
                    bg: "var(--surface-raised)",
                    borderColor: "var(--border-subtle)",
                  },
                };

                if (isCorrect) {
                  borderColor = C.correct;
                  bgColor = C.correctBg;
                  letterBg = C.correct;
                  letterColor = "white";
                  textColor = C.text;
                  choiceSx = {
                    "html[data-theme='dark'] &": {
                      bg: C.correctBgDark,
                      borderColor: C.correct,
                    },
                  };
                } else if (isWrongStudentAnswer) {
                  borderColor = C.wrong;
                  bgColor = C.wrongBg;
                  letterBg = C.wrong;
                  letterColor = "white";
                  textColor = C.text;
                  choiceSx = {
                    "html[data-theme='dark'] &": {
                      bg: C.wrongBgDark,
                      borderColor: C.wrong,
                    },
                  };
                }

                return (
                  <Flex
                    key={answer.id}
                    align="center"
                    gap={3}
                    p={3}
                    borderRadius="lg"
                    borderWidth="2px"
                    borderColor={borderColor}
                    bg={bgColor}
                    transition="border-color 0.2s, background 0.2s"
                    sx={choiceSx}
                  >
                    <Flex align="center" gap={2} flexShrink={0}>
                      <Flex
                        w="24px"
                        h="24px"
                        bg={letterBg}
                        color={letterColor}
                        borderWidth="2px"
                        borderColor={borderColor}
                        borderRadius="full"
                        align="center"
                        justify="center"
                        fontSize="sm"
                        fontWeight="800"
                      >
                        {String.fromCharCode(97 + answerIndex)}
                      </Flex>
                      {isCorrect && <CheckCircle size={20} color={C.correct} />}
                      {isWrongStudentAnswer && <XCircle size={20} color={C.wrong} />}
                    </Flex>

                    <Text
                      flex="1"
                      fontWeight={isCorrect ? "700" : "500"}
                      color={textColor}
                      sx={darkTextSx}
                    >
                      {answer?.answer}
                    </Text>

                    {isStudentAnswer && (
                      <Text
                        fontSize="xs"
                        fontWeight="800"
                        px={2}
                        py={1}
                        borderRadius="full"
                        textTransform="uppercase"
                        letterSpacing="0.04em"
                        color={isCorrect ? C.correct : C.wrong}
                        bg={isCorrect ? C.correctBg : C.wrongBg}
                        flexShrink={0}
                      >
                        Your answer
                      </Text>
                    )}
                  </Flex>
                );
              })}
            </VStack>
          </Box>
        </Flex>
      </Box>
    );
  };

  const tabButtonProps = (tab) => ({
    px: 4,
    py: 2,
    fontSize: "sm",
    fontWeight: "700",
    borderRadius: "md",
    borderBottomWidth: "2px",
    borderBottomColor: activeTab === tab ? C.primary : "transparent",
    bg: activeTab === tab ? "rgba(91, 13, 22, 0.06)" : "transparent",
    color: activeTab === tab ? C.primary : C.muted,
    _hover: {
      color: C.primary,
      bg: "rgba(91, 13, 22, 0.04)",
    },
    sx: {
      "html[data-theme='dark'] &": {
        color: activeTab === tab ? "var(--text-primary)" : "var(--text-muted)",
      },
    },
  });

  const activeList = activeTab === "top" ? filteredTopMissed : filteredMyMissed;

  return (
    <Box
      bg={C.paper}
      borderWidth="1px"
      borderColor={C.border}
      borderRadius="lg"
      boxShadow="sm"
      overflow="hidden"
      w="full"
      minW={0}
    >
      <Box borderBottomWidth="1px" borderColor={C.border}>
        <Flex align="center" gap={3} p={6} pb={4}>
          <Flex
            w="40px"
            h="40px"
            bg="rgba(155, 44, 44, 0.1)"
            borderRadius="lg"
            align="center"
            justify="center"
            flexShrink={0}
          >
            <AlertCircle size={24} color={C.wrong} />
          </Flex>
          <Heading
            size="md"
            fontWeight="800"
            color={C.primary}
            sx={darkHeadingSx}
          >
            Missed Questions
          </Heading>
        </Flex>

        <Flex gap={1} px={6}>
          <Button variant="unstyled" onClick={() => setActiveTab("top")} {...tabButtonProps("top")}>
            Top Missed Questions
          </Button>
          <Button variant="unstyled" onClick={() => setActiveTab("my")} {...tabButtonProps("my")}>
            My Missed Questions
          </Button>
        </Flex>
      </Box>

      <Box p={6}>
        {allTags.length > 0 && (
          <Box mb={6}>
            <Flex align="center" gap={2} mb={3}>
              <Tag size={16} color={C.muted} />
              <Text fontSize="sm" fontWeight="800" color={C.secondary} sx={darkHeadingSx}>
                Filter by tags
              </Text>
              {selectedTags.length > 0 && (
                <Button
                  variant="link"
                  size="xs"
                  color={C.primary}
                  fontWeight="700"
                  onClick={() => setSelectedTags([])}
                  _hover={{ color: C.secondary }}
                >
                  Clear all
                </Button>
              )}
            </Flex>

            <Flex flexWrap="wrap" gap={2}>
              {allTags.map((tag) => {
                const isSelected = selectedTags.includes(tag.id);
                return (
                  <Button
                    key={tag.id}
                    size="xs"
                    leftIcon={<Tag size={12} />}
                    onClick={() => handleTagToggle(tag.id)}
                    borderRadius="full"
                    fontWeight="700"
                    px={3}
                    py={1.5}
                    h="auto"
                    bg={isSelected ? C.primary : C.sand}
                    color={isSelected ? C.cream : C.secondary}
                    borderWidth="1px"
                    borderColor={isSelected ? C.primary : C.border}
                    _hover={{
                      bg: isSelected ? C.secondary : "rgba(91, 13, 22, 0.06)",
                      borderColor: isSelected ? C.secondary : C.primary,
                    }}
                    sx={{
                      "html[data-theme='dark'] &": {
                        color: isSelected ? C.cream : "var(--text-primary)",
                        bg: isSelected ? C.primary : "var(--surface-muted)",
                      },
                    }}
                  >
                    {tag.name}
                    {isSelected ? " ✓" : ""}
                  </Button>
                );
              })}
            </Flex>

            {selectedTags.length > 0 && (
              <Text fontSize="xs" color={C.muted} mt={2}>
                Showing {activeList.length} question(s) with selected tags
              </Text>
            )}
          </Box>
        )}

        <Text fontSize="sm" color={C.muted} mb={6}>
          {activeTab === "top"
            ? "Questions that are commonly missed by students in your class"
            : "Questions you have answered incorrectly"}
        </Text>

        <Box
          className="scroll"
          maxH="384px"
          overflowY="auto"
          overflowX="hidden"
          pr={2}
          css={scrollCss}
        >
          {activeList.length === 0 ? (
            <Text textAlign="center" py={8} color={C.muted} fontWeight="600">
              No questions found with the selected tags.
            </Text>
          ) : (
            activeList.map((questionData, index) => renderQuestion(questionData, index))
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MissedQuestions;
